// Client for the seawall.fiu.edu authentication API.
// Dev: /seawall is proxied by Vite -> https://seawall.fiu.edu
// Prod: nginx proxies /seawall -> https://seawall.fiu.edu
//
// Endpoints (see https://seawall.fiu.edu/#tag/authentication):
//   POST /register/  { email, password, "account$typeID"? } -> 201 { message }
//   POST /login/     { email, password }                     -> 200 { accessToken, message }
//                                                                 + sets HttpOnly `jwt` refresh cookie
//   GET  /refresh/   (cookie `jwt` sent automatically)        -> 200 { accessToken } | 400/403
//   GET  /logout/    (cookie `jwt` sent automatically)        -> 204 (no body)
import type {
  AccessTokenResponse,
  LoginInput,
  RegisterInput,
  RegisterResponse,
} from './authTypes';

const SEAWALL_API = import.meta.env.VITE_SEAWALL_API_URL ?? '/seawall';

async function parseError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    if (typeof data?.message === 'string') return data.message;
  } catch {
    /* body may be empty (e.g. 401/403 sent via sendStatus()) */
  }
  return `Request failed (${res.status})`;
}

export async function registerRequest(input: RegisterInput): Promise<RegisterResponse> {
  const res = await fetch(`${SEAWALL_API}/register/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email: input.email,
      password: input.password,
      ...(input.accountTypeID !== undefined ? { 'account$typeID': input.accountTypeID } : {}),
    }),
  });

  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function loginRequest(input: LoginInput): Promise<AccessTokenResponse> {
  const res = await fetch(`${SEAWALL_API}/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });

  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

/**
 * Silently exchange the HttpOnly refresh cookie for a new access token.
 * Returns null (rather than throwing) on 400/403 — both mean "not
 * authenticated", which callers should treat as a normal logged-out state.
 */
export async function refreshRequest(): Promise<AccessTokenResponse | null> {
  const res = await fetch(`${SEAWALL_API}/refresh/`, {
    method: 'GET',
    credentials: 'include',
  });

  if (res.status === 400 || res.status === 403) return null;
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

/** Revokes the refresh token server-side and clears the `jwt` cookie. Body is empty (204). */
export async function logoutRequest(): Promise<void> {
  const res = await fetch(`${SEAWALL_API}/logout/`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!res.ok && res.status !== 204) {
    throw new Error(await parseError(res));
  }
}
